<template>
  <section class="exhibition-schedule" aria-labelledby="exhibition-schedule-title">
    <div class="exhibition-schedule__content">
      <h2 id="exhibition-schedule-title" class="exhibition-schedule__title">
        Exhibition Schedule
      </h2>
      <p class="exhibition-schedule__subtitle">
        We are looking forward to your visit at the exhibition site!
      </p>

      <div ref="exhibitionSwiper" class="exhibition-swiper">
        <div class="swiper-wrapper exhibition-swiper__wrapper">
          <article
            v-for="exhibition in exhibitions"
            :key="exhibition.key"
            class="swiper-slide exhibition-swiper__slide exhibition-card"
            :class="[
              `exhibition-card--${exhibition.key}`,
              {
                'is-completed': exhibition.completed
              }
            ]"
            :role="exhibition.completed ? undefined : 'button'"
            :tabindex="exhibition.completed ? -1 : 0"
            :aria-haspopup="exhibition.completed ? undefined : 'dialog'"
            @click="handleExhibitionClick(exhibition, $event)"
            @keydown.enter.prevent="handleExhibitionClick(exhibition, $event)"
            @keydown.space.prevent="handleExhibitionClick(exhibition, $event)"
          >
            <div class="exhibition-card__content">
              <div class="exhibition-card__media">
                <img
                  class="exhibition-card__image"
                  :src="exhibition.image"
                  :alt="exhibition.alt"
                  width="330"
                  height="330"
                  loading="lazy"
                  decoding="async"
                  draggable="false"
                >
              </div>
              <div class="exhibition-card__copy">
                <h3 class="exhibition-card__title" :class="{ 'exhibition-card__title--two-lines': exhibition.titleLine2 }">
                  <span>{{ exhibition.title }}</span>
                  <span v-if="exhibition.titleLine2">{{ exhibition.titleLine2 }}</span>
                </h3>
                <p class="exhibition-card__location">{{ exhibition.location }}</p>
                <p v-if="exhibition.date" class="exhibition-card__date">{{ exhibition.date }}</p>
              </div>
            </div>
            <div v-if="exhibition.completed" class="exhibition-card__status">
              Completed
            </div>
          </article>
        </div>
      </div>

      <div class="exhibition-schedule__controls" aria-label="Exhibition carousel controls">
        <button
          ref="previousButton"
          class="exhibition-swiper__prev"
          type="button"
          aria-label="Previous exhibitions"
          :disabled="isBeginning"
          @click="showPreviousExhibitions"
        >
          <span aria-hidden="true">←</span>
        </button>
        <button
          ref="nextButton"
          class="exhibition-swiper__next"
          type="button"
          aria-label="Next exhibitions"
          :disabled="isEnd"
          @click="showNextExhibitions"
        >
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </div>
  </section>

  <Teleport to="body">
    <div
      v-if="dialogMode"
      class="exhibition-coming-soon"
      role="presentation"
      @click.self="closeDialog"
      @pointerdown="handleDialogPointerDown"
    >
      <section
        v-if="dialogMode === 'comingSoon'"
        class="exhibition-coming-soon__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="exhibition-coming-soon-title"
        aria-describedby="exhibition-coming-soon-description"
      >
        <div class="exhibition-coming-soon__content">
          <img
            class="exhibition-coming-soon__graphic"
            src="/static/images/home/exhibitions/exhibition-coming-soon.svg"
            alt=""
            width="100"
            height="100"
            aria-hidden="true"
            draggable="false"
          >
          <div class="exhibition-coming-soon__copy">
            <h2 id="exhibition-coming-soon-title" class="exhibition-coming-soon__title">
              {{ comingSoonCopy.exhibitionComingSoon.title }}
            </h2>
            <p id="exhibition-coming-soon-description" class="exhibition-coming-soon__description">
              {{ comingSoonCopy.exhibitionComingSoon.description }}
            </p>
          </div>
          <button
            ref="dialogPrimaryButton"
            class="exhibition-coming-soon__button"
            :class="{ 'is-keyboard-focus': isDialogKeyboardFocus }"
            type="button"
            @click="closeDialog"
          >
            {{ comingSoonCopy.common.ok }}
          </button>
        </div>
      </section>
      <div
        v-else-if="dialogMode === 'poster' && activeExhibition"
        class="exhibition-poster"
        role="dialog"
        aria-modal="true"
        :aria-label="comingSoonCopy.exhibitionPoster.title"
      >
        <button
          ref="dialogPrimaryButton"
          class="exhibition-poster__close"
          type="button"
          :aria-label="comingSoonCopy.exhibitionPoster.close"
          @click="closeDialog"
        />
        <img
          class="exhibition-poster__image"
          :src="activeExhibition.posterImage"
          :alt="getLocalizedText(activeExhibition.posterAltKey)"
          width="1080"
          height="1920"
          decoding="async"
          draggable="false"
        >
      </div>
    </div>
  </Teleport>
</template>

<script>
import {
  EXHIBITION_SCHEDULE_DEFAULT_LOCALE,
  EXHIBITION_SCHEDULE_MESSAGES,
  normalizeExhibitionScheduleLocale
} from './exhibition-schedule-i18n'

export default {
  name: 'ExhibitionSchedule',
  data() {
    return {
      exhibitionSwiper: null,
      activeIndex: 2,
      dialogMode: null,
      activeExhibition: null,
      dialogTriggerElement: null,
      isDialogKeyboardFocus: false,
      currentLocale: EXHIBITION_SCHEDULE_DEFAULT_LOCALE,
      languageObserver: null,
      previousBodyOverflow: '',
      previousBodyPaddingRight: '',
      isBeginning: true,
      isEnd: false,
      exhibitions: [
        {
          key: 'whx-miami',
          title: 'WHX Miami 2026',
          location: 'Miami Beach, Florida, USA',
          completed: true,
          image: '/static/images/home/exhibitions/whx-miami-2026.png',
          alt: 'WHX Miami 2026 exhibition venue'
        },
        {
          key: 'china-sport-show',
          title: 'China Sport Show 2026',
          location: 'Xiamen, China',
          completed: true,
          image: '/static/images/home/exhibitions/china-sport-show-2026.png',
          alt: 'China Sport Show 2026 exhibition venue'
        },
        {
          key: 'iwf-shenzhen',
          title: 'IWF SHENZHEN 2026',
          location: 'Shenzhen, China',
          date: 'August 14–16, 2026',
          image: '/static/images/home/exhibitions/iwf-shenzhen-2026.png',
          alt: 'IWF Shenzhen 2026 exhibition venue',
          posterImage: '/static/images/home/exhibitions/iwf-shenzhen-2026-poster.png',
          posterAltKey: 'exhibitionPoster.imageAlt'
        },
        {
          key: 'medica',
          title: 'MEDICA 2026',
          titleLine2: 'World Forum for Medicine',
          location: 'Düsseldorf, Germany',
          date: 'November 16–19, 2026',
          image: '/static/images/home/exhibitions/medica-2026.png',
          alt: 'MEDICA 2026 World Forum for Medicine exhibition venue'
        },
        {
          key: 'dubai-muscle-show',
          title: 'Dubai Muscle Show 2027',
          location: 'Dubai, United Arab Emirates',
          date: 'October 28–31, 2027',
          image: '/static/images/home/exhibitions/dubai-muscle-show-2026.png',
          alt: 'Dubai Muscle Show 2027 exhibition venue'
        }
      ]
    }
  },
  computed: {
    comingSoonCopy() {
      return EXHIBITION_SCHEDULE_MESSAGES[this.currentLocale] || EXHIBITION_SCHEDULE_MESSAGES.en
    }
  },
  mounted() {
    this.syncCurrentLocale()
    this.observeLanguageChanges()
    this.$nextTick(this.initSwiper)
  },
  beforeUnmount() {
    this.teardownDialog()
    window.removeEventListener('storage', this.handleLanguageStorageChange)
    if (this.languageObserver) {
      this.languageObserver.disconnect()
      this.languageObserver = null
    }

    if (this.exhibitionSwiper) {
      this.exhibitionSwiper.destroy(true, true)
      this.exhibitionSwiper = null
    }
  },
  methods: {
    syncCurrentLocale() {
      const globalLocale = window.i18n && window.i18n.currentLang
      const savedLocale = window.localStorage.getItem('anovator_language')
      const documentLocale = document.documentElement.getAttribute('lang')
      this.currentLocale = normalizeExhibitionScheduleLocale(globalLocale || savedLocale || documentLocale)
    },
    observeLanguageChanges() {
      this.languageObserver = new MutationObserver(this.syncCurrentLocale)
      this.languageObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['lang']
      })
      window.addEventListener('storage', this.handleLanguageStorageChange)
    },
    handleLanguageStorageChange(event) {
      if (event.key === 'anovator_language') this.syncCurrentLocale()
    },
    getLocalizedText(key) {
      if (!key) return ''

      return key.split('.').reduce((value, segment) => value && value[segment], this.comingSoonCopy) || ''
    },
    initSwiper() {
      if (this.exhibitionSwiper || !window.Swiper) return

      const container = this.$refs.exhibitionSwiper
      const previousButton = this.$refs.previousButton
      const nextButton = this.$refs.nextButton
      if (!container || !previousButton || !nextButton) return

      this.exhibitionSwiper = new window.Swiper(container, {
        initialSlide: 2,
        slidesPerView: 'auto',
        spaceBetween: 20,
        allowTouchMove: true,
        grabCursor: true,
        watchOverflow: true,
        navigation: {
          prevEl: previousButton,
          nextEl: nextButton,
          disabledClass: 'is-disabled',
          lockClass: 'is-locked'
        }
      })

      this.exhibitionSwiper.on('slideChange', this.updateSwiperState)
      this.exhibitionSwiper.on('reachBeginning', this.updateNavigationState)
      this.exhibitionSwiper.on('reachEnd', this.updateNavigationState)
      this.exhibitionSwiper.on('fromEdge', this.updateNavigationState)
      this.exhibitionSwiper.on('resize', this.updateNavigationState)
      this.updateSwiperState()
    },
    showPreviousExhibitions(event) {
      event.stopImmediatePropagation()
      if (this.exhibitionSwiper && !this.exhibitionSwiper.isBeginning) {
        this.exhibitionSwiper.slidePrev()
      }
    },
    showNextExhibitions(event) {
      event.stopImmediatePropagation()
      if (this.exhibitionSwiper && !this.exhibitionSwiper.isEnd) {
        this.exhibitionSwiper.slideNext()
      }
    },
    handleExhibitionClick(exhibition, event) {
      if (exhibition.completed) return

      const mode = exhibition.posterImage ? 'poster' : 'comingSoon'
      this.openDialog(mode, exhibition, event)
    },
    openDialog(mode, exhibition, event) {
      if (mode !== 'comingSoon' && mode !== 'poster') return

      const wasClosed = !this.dialogMode
      const eventTarget = event && event.currentTarget
      this.isDialogKeyboardFocus = false
      this.dialogMode = mode
      this.activeExhibition = exhibition
      this.dialogTriggerElement = eventTarget && typeof eventTarget.focus === 'function'
        ? eventTarget
        : document.activeElement

      if (wasClosed) {
        this.lockBodyScroll()
        document.addEventListener('keydown', this.handleDialogKeydown)
      }

      this.$nextTick(() => {
        if (this.$refs.dialogPrimaryButton) this.$refs.dialogPrimaryButton.focus()
      })
    },
    closeDialog() {
      if (!this.dialogMode) return

      this.dialogMode = null
      this.activeExhibition = null
      this.isDialogKeyboardFocus = false
      document.removeEventListener('keydown', this.handleDialogKeydown)
      this.unlockBodyScroll()

      const focusTarget = this.dialogTriggerElement
      this.dialogTriggerElement = null
      this.$nextTick(() => {
        if (focusTarget && document.contains(focusTarget)) focusTarget.focus()
      })
    },
    handleDialogKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeDialog()
      } else if (event.key === 'Tab') {
        event.preventDefault()
        this.isDialogKeyboardFocus = true
        if (this.$refs.dialogPrimaryButton) this.$refs.dialogPrimaryButton.focus()
      }
    },
    handleDialogPointerDown() {
      this.isDialogKeyboardFocus = false
    },
    lockBodyScroll() {
      this.previousBodyOverflow = document.body.style.overflow
      this.previousBodyPaddingRight = document.body.style.paddingRight

      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
      if (scrollbarWidth > 0) {
        const currentPadding = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0
        document.body.style.paddingRight = `${currentPadding + scrollbarWidth}px`
      }
      document.body.style.overflow = 'hidden'
    },
    unlockBodyScroll() {
      document.body.style.overflow = this.previousBodyOverflow
      document.body.style.paddingRight = this.previousBodyPaddingRight
    },
    teardownDialog() {
      document.removeEventListener('keydown', this.handleDialogKeydown)
      if (this.dialogMode) this.unlockBodyScroll()
      this.dialogMode = null
      this.activeExhibition = null
      this.dialogTriggerElement = null
      this.isDialogKeyboardFocus = false
    },
    updateNavigationState() {
      if (!this.exhibitionSwiper) return

      this.isBeginning = this.exhibitionSwiper.isBeginning
      this.isEnd = this.exhibitionSwiper.isEnd
    },
    updateSwiperState() {
      if (!this.exhibitionSwiper) return

      this.activeIndex = this.exhibitionSwiper.activeIndex
      this.updateNavigationState()
    }
  }
}
</script>

<style scoped>
.exhibition-schedule {
  background: #000;
  box-sizing: border-box;
  height: 801px;
  overflow: hidden;
  width: 100%;
}

.exhibition-schedule__content {
  height: 801px;
  margin: 0 auto;
  max-width: 1440px;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.exhibition-schedule__title,
.exhibition-schedule__subtitle {
  left: 50%;
  margin: 0;
  position: absolute;
  text-align: center;
  transform: translateX(-50%);
  white-space: nowrap;
}

.exhibition-schedule__title {
  color: #fff;
  font-family: 'MiSans-Bold', sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 37px;
  top: 80px;
}

.exhibition-schedule__subtitle {
  color: #fff;
  font-family: 'MiSans-Regular', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  top: 127px;
}

.exhibition-swiper {
  box-sizing: border-box;
  height: 410px;
  left: 50%;
  max-width: 1368px;
  overflow: hidden;
  padding: 38px 0 42px;
  position: absolute;
  top: 168px;
  transform: translateX(-50%);
  width: calc(100% - 72px);
}

.exhibition-swiper > .exhibition-swiper__wrapper {
  box-sizing: content-box;
  display: flex;
  height: 330px;
  position: relative;
  transition-delay: 0s;
  transition-duration: 0ms;
  transition-property: transform;
  transition-timing-function: ease;
  width: 100%;
}

.exhibition-swiper > .exhibition-swiper__wrapper > .exhibition-swiper__slide {
  background: #252525;
  box-sizing: border-box;
  display: block;
  flex: 0 0 330px;
  font-size: initial;
  height: 330px;
  max-width: 330px;
  min-width: 330px;
  position: relative;
  text-align: center;
  width: 330px;
}

.exhibition-card {
  background: #252525;
  cursor: pointer;
  height: 330px;
  isolation: isolate;
  overflow: hidden;
  position: relative;
  transition:
    background-color 220ms ease,
    transform 220ms ease,
    box-shadow 220ms ease;
  width: 330px;
}

.exhibition-card:focus-visible {
  outline: 2px solid #fff;
  outline-offset: -3px;
}

.exhibition-card:not(.is-completed):hover,
.exhibition-card:not(.is-completed):focus-visible {
  background: #ef0a6a;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.22);
  transform: translateY(-6px);
}

.exhibition-card:not(.is-completed):active {
  transform: translateY(-2px) scale(0.99);
}

.exhibition-coming-soon {
  align-items: center;
  backdrop-filter: blur(10px);
  background: rgba(0, 0, 0, 0.5);
  box-sizing: border-box;
  display: flex;
  inset: 0;
  justify-content: center;
  overflow: auto;
  padding: 24px;
  position: fixed;
  -webkit-backdrop-filter: blur(10px);
  z-index: 11000;
}

.exhibition-coming-soon__dialog {
  background: #fff;
  border-radius: 28px;
  box-sizing: border-box;
  height: 411px;
  max-height: calc(100vh - 48px);
  max-width: 636px;
  overflow-y: auto;
  width: calc(100vw - 48px);
}

.exhibition-coming-soon__content {
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin: 0 auto;
  min-height: 411px;
  padding: 60px 0;
  width: min(371px, calc(100% - 48px));
}

.exhibition-coming-soon__graphic {
  display: block;
  flex: 0 0 100px;
  height: 100px;
  max-width: none;
  width: 100px;
}

.exhibition-coming-soon__copy {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;
  width: 100%;
}

.exhibition-coming-soon__title,
.exhibition-coming-soon__description {
  margin: 0;
  width: 100%;
}

.exhibition-coming-soon__title {
  color: #000;
  font-family: 'MiSans-Bold', sans-serif;
  font-size: 30px;
  font-weight: 700;
  line-height: normal;
}

.exhibition-coming-soon__description {
  color: rgba(0, 0, 0, 0.8);
  font-family: 'MiSans-Medium', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: normal;
}

.exhibition-coming-soon__button {
  align-items: center;
  background: #ffffff;
  border: 1px solid #000000;
  border-radius: 67px;
  color: #000000;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 54px;
  font-family: 'MiSans-Regular', sans-serif;
  font-size: 14px;
  font-weight: 400;
  height: 54px;
  justify-content: center;
  line-height: normal;
  padding: 0 20px;
  transition:
    color 180ms ease,
    border-color 180ms ease,
    background-color 180ms ease,
    box-shadow 180ms ease;
  width: 134px;
}

.exhibition-coming-soon__button:focus {
  outline: none;
}

.exhibition-coming-soon__button:hover,
.exhibition-coming-soon__button.is-keyboard-focus:focus {
  background: #ffffff;
  border-color: #ef0a6a;
  color: #ef0a6a;
}

.exhibition-coming-soon__button.is-keyboard-focus:focus {
  outline: 2px solid rgba(239, 10, 106, 0.28);
  outline-offset: 3px;
}

.exhibition-poster {
  align-items: center;
  display: flex;
  justify-content: center;
  max-height: calc(100vh - 80px);
  max-width: min(560px, calc(100vw - 48px));
  position: relative;
}

.exhibition-poster__image {
  display: block;
  height: auto;
  max-height: calc(100vh - 80px);
  max-width: min(560px, calc(100vw - 48px));
  object-fit: contain;
  width: auto;
}

.exhibition-poster__close {
  align-items: center;
  background: rgba(0, 0, 0, 0.68);
  border: 0;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  display: flex;
  height: 40px;
  justify-content: center;
  padding: 0;
  position: absolute;
  right: 12px;
  top: 12px;
  transition: background-color 180ms ease;
  width: 40px;
  z-index: 1;
}

.exhibition-poster__close::before,
.exhibition-poster__close::after {
  background: currentColor;
  content: '';
  height: 2px;
  position: absolute;
  width: 18px;
}

.exhibition-poster__close::before {
  transform: rotate(45deg);
}

.exhibition-poster__close::after {
  transform: rotate(-45deg);
}

.exhibition-poster__close:hover {
  background: rgba(239, 10, 106, 0.86);
}

.exhibition-poster__close:focus {
  outline: none;
}

.exhibition-poster__close:focus-visible {
  background: rgba(239, 10, 106, 0.86);
  outline: 2px solid #fff;
  outline-offset: 3px;
}

.exhibition-swiper > .exhibition-swiper__wrapper > .exhibition-card.is-completed {
  background: transparent;
}

.exhibition-card__content {
  inset: 0;
  overflow: hidden;
  position: absolute;
}

.exhibition-card.is-completed .exhibition-card__content {
  background: #252525;
  opacity: 0.5;
}

.exhibition-card__media {
  height: 100%;
  inset: 0;
  overflow: hidden;
  position: absolute;
  width: 100%;
  z-index: 0;
}

.exhibition-card__image {
  display: block;
  height: 100%;
  left: 0;
  max-width: none;
  object-fit: cover;
  object-position: center center;
  opacity: 0.5;
  position: absolute;
  top: 0;
  transition:
    opacity 220ms ease,
    transform 320ms ease;
  user-select: none;
  width: 100%;
  z-index: 0;
}

.exhibition-card:not(.is-completed):hover .exhibition-card__image,
.exhibition-card:not(.is-completed):focus-visible .exhibition-card__image {
  opacity: 0.42;
  transform: scale(1.035);
}

.exhibition-card--whx-miami .exhibition-card__image {
  height: 124.55%;
  left: -12.12%;
  top: -6.06%;
  width: 124.55%;
}

.exhibition-card--iwf-shenzhen .exhibition-card__image {
  height: 121.8%;
  left: auto;
  right: 0;
  top: -3.1%;
  width: 216.7%;
}

.exhibition-card--china-sport-show.is-completed .exhibition-card__image {
  height: 330px;
  left: -96px;
  top: 0;
  width: 522px;
}

.exhibition-card__copy {
  inset: 0;
  position: absolute;
  z-index: 1;
}

.exhibition-card__media,
.exhibition-card__image,
.exhibition-card__copy,
.exhibition-card__status {
  pointer-events: none;
}

.exhibition-card__title,
.exhibition-card__location,
.exhibition-card__date {
  left: 12px;
  margin: 0;
  position: absolute;
  right: 12px;
  text-align: center;
}

.exhibition-card__title {
  color: #fff;
  display: flex;
  flex-direction: column;
  font-family: 'MiSans-Bold', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 27px;
  top: 30px;
}

.exhibition-card__location {
  color: rgba(255, 255, 255, 0.5);
  font-family: 'MiSans-Regular', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  top: 63px;
}

.exhibition-card__title--two-lines + .exhibition-card__location {
  top: 90px;
}

.exhibition-card__date {
  bottom: 30px;
  color: #fff;
  font-family: 'MiSans-Bold', sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
}

.exhibition-card__status {
  align-items: center;
  background: #4b4b4b;
  bottom: 0;
  color: #fff;
  display: flex;
  font-family: 'MiSans-Bold', sans-serif;
  font-size: 12px;
  font-weight: 700;
  height: 40px;
  justify-content: center;
  left: 0;
  line-height: 16px;
  position: absolute;
  width: 330px;
  z-index: 3;
}

.exhibition-schedule__controls {
  display: flex;
  gap: 20px;
  position: absolute;
  right: 72px;
  top: 606px;
}

.exhibition-swiper__prev,
.exhibition-swiper__next {
  align-items: center;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.68);
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  font-size: 22px;
  height: 46px;
  justify-content: center;
  line-height: 1;
  padding: 0;
  transition: background-color 0.25s ease, border-color 0.25s ease, opacity 0.25s ease;
  width: 46px;
}

.exhibition-swiper__prev:hover:not(:disabled),
.exhibition-swiper__next:hover:not(:disabled) {
  background: #ee0b6a;
  border-color: #ee0b6a;
}

.exhibition-swiper__prev:focus,
.exhibition-swiper__next:focus {
  outline: none;
}

.exhibition-swiper__prev:focus-visible,
.exhibition-swiper__next:focus-visible {
  outline: 2px solid #ee0b6a;
  outline-offset: 3px;
}

.exhibition-swiper__prev:disabled,
.exhibition-swiper__next:disabled,
.exhibition-swiper__prev.is-disabled,
.exhibition-swiper__next.is-disabled,
.exhibition-swiper__prev.is-locked,
.exhibition-swiper__next.is-locked {
  cursor: not-allowed;
  opacity: 0.3;
}

@media (max-width: 1200px) {
  .exhibition-swiper {
    width: calc(100% - 48px);
  }

  .exhibition-schedule__controls {
    right: 24px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .exhibition-swiper__wrapper,
  .exhibition-swiper__prev,
  .exhibition-swiper__next,
  .exhibition-card,
  .exhibition-card__image {
    transition: none;
  }

  .exhibition-card:not(.is-completed):hover,
  .exhibition-card:not(.is-completed):focus-visible,
  .exhibition-card:not(.is-completed):active,
  .exhibition-card:not(.is-completed):hover .exhibition-card__image,
  .exhibition-card:not(.is-completed):focus-visible .exhibition-card__image {
    transform: none;
  }
}
</style>
